import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import classNames from 'classnames';
import { Tooltip } from 'antd';
import { useDispatch } from 'react-redux';
import { useAuth } from '@hooks/useAuth';
import { toggleSingInExternalReviewerModal } from '@redux/actions/modalAction';
import type { IAssetViewerStateContextValue } from '@context/AssetViewerStateContext';
import { withKeysCanvasHistoryCopy } from '@helpers/withKeysCanvasHistoryChange';
import { useAssetMVPermissions } from '@hooks';
import {
  PIN_DRAW_TOOL,
  CURSOR_DRAW_TOOL
} from '@pages/MediaViewer/helpers/drawToolShape';
import {
  ShapesDrawingToolsCommentIcon,
  ShapesDrawingToolsCursorIcon,
  ShapesDrawingToolsShowCommentIcon
} from '@icons';

import './CompareVersionTools.less';

export type CompareVersionToolsProps = {
  leftAssetViewerState: IAssetViewerStateContextValue | null;
  rightAssetViewerState: IAssetViewerStateContextValue | null;
};

function CompareVersionTools({
  leftAssetViewerState,
  rightAssetViewerState
}: CompareVersionToolsProps) {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const canLeaveComment = useAssetMVPermissions('comment');
  const isHideComments =
    leftAssetViewerState?.isHideComments ??
    rightAssetViewerState?.isHideComments;
  const shapesDrawingTools =
    leftAssetViewerState?.shapesDrawingTools ??
    rightAssetViewerState?.shapesDrawingTools;
  const canvasMode =
    leftAssetViewerState?.canvasMode ?? rightAssetViewerState?.canvasMode;
  const selectedCommentId =
    leftAssetViewerState?.selectedCommentId ??
    rightAssetViewerState?.selectedCommentId;
  const isCommentFieldFocused =
    leftAssetViewerState?.isCommentFieldFocused ??
    rightAssetViewerState?.isCommentFieldFocused;
  const isToolbarExpanded =
    leftAssetViewerState?.isMobileDrawingToolbarOpen ??
    rightAssetViewerState?.isMobileDrawingToolbarOpen ??
    false;

  const assetType =
    leftAssetViewerState?.selectedVersion?.metadata.type ??
    rightAssetViewerState?.selectedVersion?.metadata.type;
  const shouldHideCommentsButton =
    assetType === 'video' || assetType === 'audio' || assetType === 'website';

  const undoRedoTooltips = useMemo(() => {
    return withKeysCanvasHistoryCopy();
  }, []);

  const setHideComments = useCallback(() => {
    leftAssetViewerState?.setHideComments(!isHideComments);
    rightAssetViewerState?.setHideComments(!isHideComments);
  }, [
    isHideComments,
    leftAssetViewerState?.setHideComments,
    rightAssetViewerState?.setHideComments
  ]);

  const selectComment = useCallback(
    (id: string | null) => {
      leftAssetViewerState?.selectComment(id);
      rightAssetViewerState?.selectComment(id);
    },
    [leftAssetViewerState?.selectComment, rightAssetViewerState?.selectComment]
  );

  const setDocumentHighlight = useCallback(() => {
    leftAssetViewerState?.setDocumentHighlight(null);
    rightAssetViewerState?.setDocumentHighlight(null);
  }, [
    leftAssetViewerState?.setDocumentHighlight,
    rightAssetViewerState?.setDocumentHighlight
  ]);

  const setCanvasMode = useCallback(
    (mode: 'view' | 'edit') => {
      leftAssetViewerState?.setCanvasMode(mode);
      rightAssetViewerState?.setCanvasMode(mode);
    },
    [leftAssetViewerState?.setCanvasMode, rightAssetViewerState?.setCanvasMode]
  );

  const setDrawToolsShape = useCallback(
    (shape: string) => {
      leftAssetViewerState?.setDrawToolsShape(shape);
      rightAssetViewerState?.setDrawToolsShape(shape);
    },
    [
      leftAssetViewerState?.setDrawToolsShape,
      rightAssetViewerState?.setDrawToolsShape
    ]
  );

  const dismissAnnotation = useCallback(() => {
    leftAssetViewerState?.dismissAnnotation();
    rightAssetViewerState?.dismissAnnotation();
  }, [
    leftAssetViewerState?.dismissAnnotation,
    rightAssetViewerState?.dismissAnnotation
  ]);

  const onShapeClick = useCallback(
    (shape: string) => {
      if (shape === CURSOR_DRAW_TOOL) {
        dismissAnnotation();
        setDrawToolsShape(CURSOR_DRAW_TOOL);
        return;
      }
      if (!user) {
        dispatch(
          toggleSingInExternalReviewerModal({
            visible: true
          })
        );
        return;
      }
      if (shape === PIN_DRAW_TOOL) {
        setDrawToolsShape(shape);
        return;
      }
      if (canvasMode === 'view') {
        if (selectedCommentId !== 'reply') {
          selectComment('new');
          setDocumentHighlight();
        }
        setCanvasMode('edit');
      }
      setDrawToolsShape(shape);
    },
    [
      canvasMode,
      dispatch,
      selectedCommentId,
      user,
      dismissAnnotation,
      setDrawToolsShape,
      selectComment,
      setDocumentHighlight,
      setCanvasMode
    ]
  );

  const onShapeClickRef = useRef(onShapeClick);
  onShapeClickRef.current = onShapeClick;
  useEffect(() => {
    if (
      (rightAssetViewerState?.shapesDrawingTools?.shape === CURSOR_DRAW_TOOL &&
        leftAssetViewerState?.shapesDrawingTools?.shape !== CURSOR_DRAW_TOOL) ||
      (leftAssetViewerState?.shapesDrawingTools?.shape !== CURSOR_DRAW_TOOL &&
        rightAssetViewerState?.shapesDrawingTools?.shape === CURSOR_DRAW_TOOL)
    ) {
      onShapeClickRef.current('cursor');
    }
  }, [
    leftAssetViewerState?.shapesDrawingTools?.shape,
    rightAssetViewerState?.shapesDrawingTools?.shape
  ]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const fieldElement = document.querySelector(
        '.filter-sort-panel-search-input'
      );
      const transcriptSearchInput = document.getElementById(
        'TRANSCRIPT_SEARCH_INPUT'
      );

      const key = e.key.toLowerCase();
      if ((e.metaKey || e.ctrlKey) && key === 'h') {
        e.preventDefault();
        setHideComments();
        return;
      }
      if (fieldElement || transcriptSearchInput || isCommentFieldFocused) {
        return;
      }
      if (key === 'escape' || key === 'v') {
        onShapeClick(CURSOR_DRAW_TOOL);
        return;
      }
      if (key === 'c' && canLeaveComment) {
        onShapeClick(PIN_DRAW_TOOL);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [setHideComments, onShapeClick, isCommentFieldFocused, canLeaveComment]);

  return (
    <div
      className={classNames(
        isToolbarExpanded && 'compare_version_tools_container_open',
        'compare_version_tools_container'
      )}
    >
      <div className="wrapper">
        <Tooltip
          title={
            <div className="media-viewer-tooltip-inner">
              Select
              <span className="media-viewer-tooltip-label">V</span>
            </div>
          }
          overlayInnerStyle={{ padding: 0 }}
          placement="right"
          overlayClassName="toggle_favorite_overlay"
        >
          <span
            role="button"
            tabIndex={0}
            aria-label="Cursor"
            className={classNames(
              shapesDrawingTools?.shape === CURSOR_DRAW_TOOL && 'selected',
              'anticon anticon-font-size'
            )}
            onClick={() => {
              onShapeClick(CURSOR_DRAW_TOOL);
            }}
          >
            <ShapesDrawingToolsCursorIcon />
          </span>
        </Tooltip>

        {canLeaveComment && (
          <Tooltip
            title={
              <div className="media-viewer-tooltip-inner">
                Comment
                <span className="media-viewer-tooltip-label">C</span>
              </div>
            }
            overlayInnerStyle={{ padding: 0 }}
            placement="right"
            overlayClassName="toggle_favorite_overlay"
          >
            <span
              role="button"
              tabIndex={0}
              aria-label="Add comment"
              className={classNames(
                shapesDrawingTools?.shape === PIN_DRAW_TOOL && 'selected',
                'anticon anticon-font-size'
              )}
              onClick={() => {
                onShapeClick(PIN_DRAW_TOOL);
              }}
            >
              <ShapesDrawingToolsCommentIcon />
            </span>
          </Tooltip>
        )}

        {!shouldHideCommentsButton && (
          <>
            <span className="divider b-full" />
            <Tooltip
              title={
                <div className="media-viewer-tooltip-inner">
                  {isHideComments ? 'Show' : 'Hide'} comments
                  <span className="media-viewer-tooltip-label">
                    {undoRedoTooltips.toggleShowComments}
                  </span>
                </div>
              }
              overlayInnerStyle={{ padding: 0 }}
              placement="right"
              overlayClassName="toggle_favorite_overlay"
            >
              <span
                role="button"
                tabIndex={0}
                aria-label="Toggle comments visibility"
                onClick={setHideComments}
                className={classNames(
                  isHideComments && 'selected',
                  'anticon anticon-font-size shapes_drawing_tools-item'
                )}
              >
                <ShapesDrawingToolsShowCommentIcon />
              </span>
            </Tooltip>
          </>
        )}
      </div>
    </div>
  );
}
export default CompareVersionTools;
